import type { Components } from '@mildang/mui';
import { CSSObject } from '@emotion/react';
import { inputBaseClasses } from '@mildang/mui';
import type { Theme } from '@mildang/mui';

const IOS_MAX_FONT_SIZE = 16;

interface InputBaseStyleArgs {
  paddingLeft: number;
  paddingBottom: number;
  paddingTop: number;
  lineHeight: number;
  height: number;
  rootHeight: number;
  fontSize: number;
  typography: Theme['typography'];
}

export const getIOSScaleDownRatioByFontSize = (fontSize: number) => (value: number) => {
  const scaleDownRatio = fontSize / IOS_MAX_FONT_SIZE;
  return value / scaleDownRatio;
};
function createScaleDownInputBaseStyle(style: InputBaseStyleArgs, isLarge?: boolean) {
  const scaleDownRatio = getIOSScaleDownRatioByFontSize(style.fontSize);
  const height = scaleDownRatio(style.height);

  const textFieldHeight = style.height + style.paddingBottom + style.paddingTop;

  return {
    height: style.rootHeight,
    input: {
      paddingLeft: `${scaleDownRatio(style.paddingLeft)}px`,
      paddingBottom: `${scaleDownRatio(style.paddingBottom)}px`,
      paddingTop: `${scaleDownRatio(style.paddingTop)}px`,
      fontSize: style.typography.pxToRem(IOS_MAX_FONT_SIZE),
      lineHeight: scaleDownRatio(style.lineHeight / style.fontSize),
      transformOrigin: 'left center',
      width: `${scaleDownRatio(100)}%`,
      marginRight: `${(1 - scaleDownRatio(1)) * 100}%`,
      transform: `scale(${style.fontSize / IOS_MAX_FONT_SIZE})`,
      height: `${height}px`,
      marginBottom: `-${(scaleDownRatio(1) / -1) * textFieldHeight}px`,
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: scaleDownRatio(8),
    },
    [`&.${inputBaseClasses.multiline}`]: {
      height: 'initial',
      ...(isLarge && {
        paddingLeft: `${style.paddingLeft}px`,
        paddingBottom: `${style.paddingBottom}px`,
        paddingTop: `${style.paddingTop}px`,
        fontSize: style.fontSize,
        lineHeight: style.lineHeight / style.fontSize,
      }),
    },
  } as CSSObject;
}
// eslint-disable-next-line
const createTextField = ({ palette, typography, transitions, opacity }: Theme): Components => ({
  MuiTextField: {
    defaultProps: {
      size: 'xLarge',
    },
  },

  MuiInputBase: {
    variants: [
      {
        props: {
          size: 'xLarge',
        },
        style: {
          input: {
            lineHeight: 26 / 16,
            height: 28,
            paddingBottom: 11,
            paddingTop: 11,
            paddingLeft: 12,
          },
        },
      },
      {
        props: { size: 'large' },
        style: {
          ...createScaleDownInputBaseStyle(
            {
              rootHeight: 42,
              paddingBottom: 8.01,
              paddingTop: 8.01,
              paddingLeft: 12,
              fontSize: 15,
              height: 26,
              lineHeight: 26,
              typography,
            },
            true,
          ),
        },
      },

      {
        props: { size: 'medium' },
        style: {
          ...createScaleDownInputBaseStyle({
            rootHeight: 36,
            paddingBottom: 6.01,
            paddingTop: 6.01,
            paddingLeft: 12,
            fontSize: 14,
            height: 24,
            lineHeight: 24,
            typography,
          }),
        },
      },
      {
        props: { size: 'small' },
        style: {
          ...createScaleDownInputBaseStyle({
            rootHeight: 32,
            paddingBottom: 5.01,
            paddingTop: 5.01,
            paddingLeft: 12,
            fontSize: 13,
            height: 22,
            lineHeight: 22,
            typography,
          }),
        },
      },
    ],
    styleOverrides: {
      root: {
        ...typography.body2,
        color: palette.text.tertiary,
        lineHeight: '1.5em', // Reset (24px), match the native input line-height
        '&.MuiInputBase-fullWidth': {
          maxWidth: '100%',
        },
        borderRadius: 8,
      },
      multiline: {
        fontSize: typography.pxToRem(18),
      },
      input: {
        height: '1.5em', // Reset (24px), match the native input line-height
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: palette.common.white,
        color: palette.text.primary,
        // 기본 notchedOutline 스타일
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.stroke,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.strokeHover,
          borderWidth: 1,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.primary.main,
          borderWidth: 1,
        },
        // Error notchedOutline 스타일
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.error.stroke,
        },
        '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.error.strokeHover,
        },
        '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: palette.error.main,
          borderWidth: 1,
        },
        '&.Mui-error .MuiInputAdornment-outlined svg': {
          color: palette.error.dark,
        },
      },
      notchedOutline: {
        transition: transitions.create(['border-color'], {
          duration: transitions.duration.shorter,
          easing: transitions.easing.easeOut,
        }),
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
      },
    },
  },
  MuiInputLabel: {
    defaultProps: {},
    styleOverrides: {
      root: {
        ...typography.body2,
        fontWeight: 400,
        color: palette.text.tertiary,
        '&.Mui-focused': {
          color: palette.text.secondary,
        },
        '&.Mui-error': {
          color: palette.text.tertiary,
        },

        '&.Mui-error.MuiInputLabel-shrink': {
          color: palette.error.darkActive,
        },
        '&.Mui-error.Mui-focused.MuiInputLabel-shrink': {
          color: palette.error.dark,
        },
      },
      outlined: {
        transform: 'translate(14px, 11px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75) !important',
        },
      },
      asterisk: {
        color: palette.error.main,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        ...typography.subtitle3_M,
        color: palette.text.primary,
        marginBottom: 8,
      },
      asterisk: {
        color: palette.error.main,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        marginLeft: 3,
        ...typography.caption2_R,
        color: palette.text.tertiary,
        '&.Mui-error': {
          color: palette.error.dark,
        },
      },
      error: {},
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        ...typography.body4,
      },
    },
  },
});

export default createTextField;
